var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { safePolygon, useDismiss, useFocus, useHover, useRole } from '@floating-ui/react-dom-interactions';
import clsx from 'clsx';
import { isNil, useClasses, useControlledStateWarning, useLayoutProps, useProps, } from '@hawkins/helpers';
import { FloatingUI } from '../floatingUi';
import { useCombineRefs } from '../hooks';
import { useTooltipStyles } from './tooltip.styles';
var displayName = 'Tooltip';
export var TOOLTIP_DEFAULT_DELAY_MS = 100;
export var Tooltip = React.forwardRef(function (_a, ref) {
    var _b;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _c = useProps(displayName, props), 
    // Floating UI's `blockPointerEvents` option manipulates the document body's pointer-events style
    // (see https://github.com/floating-ui/floating-ui/blob/master/packages/react-dom-interactions/src/hooks/useHover.ts#L304),
    // so out of an abundance of caution, default it to false.
    _d = _c.blockPointerEvents, 
    // Floating UI's `blockPointerEvents` option manipulates the document body's pointer-events style
    // (see https://github.com/floating-ui/floating-ui/blob/master/packages/react-dom-interactions/src/hooks/useHover.ts#L304),
    // so out of an abundance of caution, default it to false.
    blockPointerEvents = _d === void 0 ? false : _d, classesProp = _c.classes, className = _c.className, description = _c.description, _e = _c.disabled, disabled = _e === void 0 ? false : _e, _f = _c.enterDelay, enterDelay = _f === void 0 ? TOOLTIP_DEFAULT_DELAY_MS : _f, floatingUiProps = _c.floatingUiProps, _g = _c.interactive, interactive = _g === void 0 ? false : _g, label = _c.label, layout = _c.layout, _h = _c.leaveDelay, leaveDelay = _h === void 0 ? TOOLTIP_DEFAULT_DELAY_MS : _h, onClose = _c.onClose, onOpen = _c.onOpen, openProp = _c.open, _j = _c.placement, placement = _j === void 0 ? 'bottom' : _j, styleProp = _c.style, restProps = __rest(_c, ["blockPointerEvents", "classes", "className", "description", "disabled", "enterDelay", "floatingUiProps", "interactive", "label", "layout", "leaveDelay", "onClose", "onOpen", "open", "placement", "style"]);
    useControlledStateWarning(openProp, { displayName: displayName, stringifiedNullValue: 'false' });
    // Enforce only child.
    var child = React.Children.only(children);
    var descriptions = Array.isArray(description) ? description : [description];
    var enabled = !disabled;
    var isControlled = !isNil(openProp);
    // HACK: Because our Floating-UI based components have <FloatingUI> as a child,
    // we need to use a function to dynamically generate our Floating UI interactions array
    // so the parent component can have access to the `context` object (which is generated in the child <FloatingUI>).
    //
    // TODO(v5): Use the `useFloating()` and `useInteractions()` hooks directly in each component
    // rather than have them all render a <FloatingUI> as a child.
    var getFloatingUIInteractions = React.useCallback(function (_a) {
        var context = _a.context;
        return [
            /* eslint-disable react-hooks/rules-of-hooks */
            useDismiss(context, { enabled: enabled }),
            useFocus(context, { enabled: enabled }),
            useHover(context, {
                delay: {
                    close: leaveDelay,
                    open: enterDelay,
                },
                enabled: enabled,
                handleClose: interactive ? safePolygon({ blockPointerEvents: blockPointerEvents }) : null,
                // HACK: Setting `move: true` sometimes causes `onOpenChange`
                // to be called twice in quick succession when the tooltip is opened on hover.
                move: false,
            }),
            useRole(context, { enabled: enabled, role: 'tooltip' }),
            /* eslint-enable react-hooks/rules-of-hooks */
        ];
    }, [blockPointerEvents, enabled, enterDelay, interactive, leaveDelay]);
    // Refs
    var referenceRef = React.useRef();
    var internalChildRef = React.useRef(null);
    var childRef = child.ref;
    var combinedChildRef = useCombineRefs([internalChildRef, childRef]);
    var childRefWithReference = React.useCallback(function (el) {
        var _a;
        // Since Floating UI's `reference` ref is only accessible through the `renderTrigger` function,
        // use a ref to be able to access it within a memoized callback.
        (_a = referenceRef.current) === null || _a === void 0 ? void 0 : _a.call(referenceRef, el);
        combinedChildRef(el);
    }, [combinedChildRef]);
    var renderTrigger = React.useCallback(function (_a) {
        var floatingId = _a.floatingId, getReferenceProps = _a.getReferenceProps, reference = _a.reference;
        // Make sure the `reference` ref is always up-to-date.
        referenceRef.current = reference;
        var overrideProps = getReferenceProps(__assign(__assign({}, child.props), { ref: childRefWithReference }));
        // HACK: Floating UI generates its own element ID and currently doesn't allow for customization,
        // so make sure it doesn't override the user's custom ID (if any).
        if (overrideProps['aria-describedby'] && floatingId) {
            overrideProps['aria-describedby'] = floatingId;
        }
        return React.cloneElement(child, overrideProps);
    }, [child, childRefWithReference]);
    // State
    var _k = __read(React.useState(false), 2), internalOpen = _k[0], setInternalOpen = _k[1];
    var open = isControlled ? openProp : internalOpen;
    var onOpenChange = React.useCallback(function (newOpen) {
        if (!isControlled)
            setInternalOpen(newOpen);
        // HACK: Because we're no longer handling the show/hide functionality,
        // we don't have access to the underlying event, so we must create a fake one for basic backwards compatibility.
        //
        // TODO(v5): Remove the event argument from the `onClose` and `onOpen` types.
        // Having a ChangeEvent argument doesn't really make sense anyway,
        // as the triggering event for this historically was a KeyboardEvent or MouseEvent--not a ChangeEvent.
        var ev = {
            bubbles: false,
            cancelable: false,
            currentTarget: internalChildRef.current,
            defaultPrevented: false,
            eventPhase: 0,
            isDefaultPrevented: function () { return false; },
            isPropagationStopped: function () { return false; },
            isTrusted: false,
            nativeEvent: new Event('change'),
            persist: function () { return undefined; },
            preventDefault: function () { return undefined; },
            stopPropagation: function () { return undefined; },
            target: internalChildRef.current,
            timeStamp: Date.now(),
            type: 'change',
        };
        if (newOpen) {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen(ev);
        }
        else {
            onClose === null || onClose === void 0 ? void 0 : onClose(ev);
        }
    }, [isControlled, onClose, onOpen]);
    // Styles
    var baseClasses = useTooltipStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, classes.root, className, (_b = {},
        _b[classes.rootInteractive] = interactive,
        _b[classes.tooltipPlacementBottom] = placement.startsWith('bottom'),
        _b[classes.tooltipPlacementLeft] = placement.startsWith('left'),
        _b[classes.tooltipPlacementRight] = placement.startsWith('right'),
        _b[classes.tooltipPlacementTop] = placement.startsWith('top'),
        _b));
    return (_jsx(FloatingUI, __assign({ anchor: internalChildRef.current, className: rootClassName, disabled: disabled, getFloatingUIInteractions: getFloatingUIInteractions, onOpenChange: onOpenChange, open: open, placement: placement, ref: ref, renderTrigger: renderTrigger, scrollWith: true, style: style }, restProps, floatingUiProps, { children: (Array.isArray(label) ? label : [label]).map(function (item, idx) {
            var itemDescription = descriptions[idx];
            return (_jsxs("div", __assign({ className: classes.labelDescriptionContainer }, { children: [_jsx("div", __assign({ className: classes.label }, { children: item })), !isNil(itemDescription) && _jsx("div", __assign({ className: classes.description }, { children: itemDescription }))] }), "tooltip-label-description-".concat(idx)));
        }) })));
});
Tooltip.displayName = displayName;
